import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { APIResponse } from '../models/APIResponse';
import { AnalysesMethodsDetails, DepthMeasurementType, ExperimentModel, FinalReportDetailsModel, FinalSampleInputsModel, ImportTemplateResponse, ProjectSampleCreationModel, SampleCreationModel, SamplingInformation, SamplingType, SaturationPressureType } from '../models/sample';
import { apiV1 } from '../common/texts-constants';

type ApiUnknown = APIResponse<unknown>;
@Injectable({
  providedIn: 'root',
})
export class SampleCreationService {
  private http = inject(HttpClient);
  router = inject(Router);
  private apiBaseUrl = environment.apiUrl+apiV1;
  private wellIdSubject = new BehaviorSubject<string>('');

  private sampleDetailsSubject = new BehaviorSubject<SampleCreationModel | null>(null);
  public sampleDetails$ = this.sampleDetailsSubject.asObservable();

  CreateSample(sampleCreationModel: SampleCreationModel, action: string): Observable<APIResponse<SampleCreationModel>> {
    const params = new HttpParams().set('action', action);
    return this.http.post<APIResponse<SampleCreationModel>>(
      `${this.apiBaseUrl}SampleCreation/CreateSample`,
      sampleCreationModel,
      { params }
    );
  }

  CreateProjectWithSample(sampleCreationModel: ProjectSampleCreationModel, action: string): Observable<APIResponse<ProjectSampleCreationModel>> {
    const params = new HttpParams().set('action', action);
    return this.http.post<APIResponse<ProjectSampleCreationModel>>(
      `${this.apiBaseUrl}SampleCreation/CreateProjectWithSample`,
      sampleCreationModel,
      { params }
    );
  }

  GetSampleDetails(labTestId: number): Observable<SampleCreationModel> {
    return this.http
      .get<APIResponse<SampleCreationModel>>(
        `${this.apiBaseUrl}SampleCreation/GetSampleDetails/${labTestId}`
      )
      .pipe(
        map((resp) => {
          if (!resp?.isSuccess || !resp?.result) {
            throw new Error(resp?.displayMessage || 'Failed to load sample details');
          }
          this.sampleDetailsSubject.next(resp.result);
          return resp.result;
        })
      );
  }

  GetSamplingTypes(): Observable<APIResponse<SamplingType[]>> {
    return this.http.get<APIResponse<SamplingType[]>>(
      this.apiBaseUrl + 'SampleCreation/GetSamplingTypeDropdowns'
    );
  }

  GetSaturationPressureTypes(): Observable<APIResponse<SaturationPressureType[]>> {
    return this.http.get<APIResponse<SaturationPressureType[]>>(
      this.apiBaseUrl + 'SampleCreation/GetSaturationPressureTypes'
    );
  }

  ImportTemplate(formData: FormData, labTestId?: number, repeatId?: number, isRepeat = false): Observable<unknown> {
    if (labTestId !== undefined && labTestId !== null) {
      formData.append('labTestId', labTestId.toString());
    }
    if (repeatId !== undefined && repeatId !== null) {
      formData.append('RepeatId', repeatId.toString());
    }
    formData.append('isRepeat', isRepeat.toString());

    return this.http.post<unknown>(
      this.apiBaseUrl +'ExperimentTemplate/importTemplate',
      formData
    );
  }

  ImportTemplateV2(formData: FormData, labTestId?: number, repeatId?: number, isRepeat = false): Observable<ImportTemplateResponse> {
    if (labTestId !== undefined && labTestId !== null) {
      formData.append('labTestId', labTestId.toString());
    }
    if (repeatId !== undefined && repeatId !== null) {
      formData.append('RepeatId', repeatId.toString());
    }
    formData.append('isRepeat', isRepeat.toString());

    return this.http.post<ImportTemplateResponse>(
      this.apiBaseUrl +'ExperimentTemplate/importTemplatev2',
      formData
    );
  }

  wellId$ = this.wellIdSubject.asObservable();

  SetWellId(id: string): void {
    this.wellIdSubject.next(id);
  }

  GetWellId(): string {
    return this.wellIdSubject.value;
  }

  GetSampleDetailsForFinalReport(labTestId: number, projectId: number): Observable<ApiUnknown> {
    return this.http.get<ApiUnknown>(
      this.apiBaseUrl +
      'SampleCreation/GetSampleCreationFinalReport' +
      '?labTestId=' +
      labTestId +
      '&projectId=' +
      projectId
    );
  }

  GetFinalSampleInputs(labTestId: number) {
    return this.http.get<APIResponse<FinalSampleInputsModel>>(`${this.apiBaseUrl}SampleCreation/GetFinalSampleInputs/${labTestId}`);
  }

  GetSamplingInformation(labTestId: number) {
    return this.http.get<APIResponse<SamplingInformation>>(`${this.apiBaseUrl}SampleCreation/GetSamplingInformation/${labTestId}`);
  }

  GetFinalReportDetails(labTestId: number) {
    return this.http.get<APIResponse<FinalReportDetailsModel>>(
      `${this.apiBaseUrl}SampleCreation/GetFinalReportDetails/${labTestId}`
    );
  }

  SaveSamplingInformation(samplingInformation: SamplingInformation): Observable<ApiUnknown> {
    return this.http.post<ApiUnknown>(`${this.apiBaseUrl}SampleCreation/SaveSamplingInformation`, samplingInformation);
  }

  SaveFinalSampleInputs(finalSampleInputsModel: FinalSampleInputsModel, action: string): Observable<ApiUnknown> {
    const params = new HttpParams().set('action', action);
    return this.http.post<ApiUnknown>(
      `${this.apiBaseUrl}SampleCreation/SaveFinalSampleInputs`,
      finalSampleInputsModel,
      { params }
    );
  }

  SaveFinalReportDetails(
    finalReportDetailsModel: FinalReportDetailsModel,
    action: string
  ): Observable<ApiUnknown> {
    const params = new HttpParams().set('action', action);
    return this.http.post<ApiUnknown>(
      `${this.apiBaseUrl}SampleCreation/SaveFinalReportDetails`,
      finalReportDetailsModel,
      { params }
    );
  }

  UpdateFinalSampleInputs(finalSampleInputsModel: FinalSampleInputsModel, action: string): Observable<ApiUnknown> {
    const params = new HttpParams().set('action', action);
    return this.http.post<ApiUnknown>(
      `${this.apiBaseUrl}SampleCreation/UpdateFinalSampleInputs`,
      finalSampleInputsModel,
      { params }
    );
  }

  GetDepthMeasurementTypeDropdowns(): Observable<APIResponse<DepthMeasurementType[]>> {
    return this.http.get<APIResponse<DepthMeasurementType[]>>(this.apiBaseUrl + 'SampleCreation/GetDepthMeasurementTypeDropdowns');
  }


  SaveAnalysesMethodsDetails(payload: AnalysesMethodsDetails): Observable<ApiUnknown> {
    return this.http.post<ApiUnknown>(
      `${this.apiBaseUrl}SampleCreation/SaveAnalysesMethodsDetails`,
      payload
    );
  }

  GetAnalysesMethodsDetails(labTestId: number): Observable<APIResponse<AnalysesMethodsDetails>> {
    return this.http.get<APIResponse<AnalysesMethodsDetails>>(
      `${this.apiBaseUrl}SampleCreation/GetAnalysesMethodsDetails/${labTestId}`
    );
  }

  GetLabTestRepeatList(labTestId: number): Observable<APIResponse<ExperimentModel[]>> {
    return this.http.get<APIResponse<ExperimentModel[]>>(
      `${this.apiBaseUrl}SampleCreation/GetLabTestRepeatList/${labTestId}`
    );
  }
}
